/**
 * Bloco estratégico de ações corretivas — substitui aparência de tabela administrativa.
 */
import React from 'react';
import { Plus, ClipboardList, AlertCircle, ChevronRight, User, Calendar } from 'lucide-react';
import { ACTION_STATUS_LABELS, type EnterpriseAction } from '../../services/actionService';
import { actionCompletionPct, isActionOverdue } from '../../utils/issueProgress';

const STATUS_DOT: Record<string, string> = {
  pending: 'bg-slate-400',
  in_progress: 'bg-blue-500',
  completed: 'bg-emerald-500',
  blocked: 'bg-red-500',
  cancelled: 'bg-slate-300',
};

interface ProblemActionsBlockProps {
  actions: EnterpriseAction[];
  assigneeNames: Record<string, string>;
  compact?: boolean;
  variant?: 'structured' | 'quick';
  readOnly?: boolean;
  onRowClick: (action: EnterpriseAction) => void;
  onCreateAction: () => void;
}

export const ProblemActionsBlock: React.FC<ProblemActionsBlockProps> = ({
  actions,
  assigneeNames,
  compact = false,
  variant = 'structured',
  readOnly = false,
  onRowClick,
  onCreateAction,
}) => {
  if (actions.length === 0) {
    return (
      <div className="rounded border border-dashed border-slate-300 bg-slate-50/50 px-4 py-5 dark:border-slate-600 dark:bg-slate-900/30">
        <div className="flex items-start gap-3">
          <div className="rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-800">
            <ClipboardList size={18} className="text-slate-400" />
          </div>
          <div className="flex-1">
            <p className="text-[12px] font-semibold text-slate-800 dark:text-slate-200">
              {variant === 'quick' ? 'Nenhuma ação ainda' : 'Plano de ação pendente'}
            </p>
            <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
              {variant === 'quick'
                ? 'Registre a correção, com responsável e prazo. Em seguida o caso pode ser encerrado.'
                : 'Registre ações corretivas vinculadas à causa raiz para tratar o problema de forma estruturada.'}
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-[10px] text-slate-400">
              <span className="rounded bg-white px-2 py-0.5 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">Definir responsável</span>
              <span className="rounded bg-white px-2 py-0.5 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">Estabelecer prazo</span>
              <span className="rounded bg-white px-2 py-0.5 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">Acompanhar execução</span>
            </div>
            <button
              type="button"
              onClick={onCreateAction}
              className={`mt-4 flex items-center gap-1.5 rounded bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-blue-700 ${readOnly ? 'hidden' : ''}`}
            >
              <Plus size={12} /> Criar primeira ação corretiva
            </button>
          </div>
        </div>
      </div>
    );
  }

  const visible = compact ? actions.slice(0, 5) : actions;
  const completed = actions.filter(a => a.status === 'completed').length;
  const overdue = actions.filter(a => isActionOverdue(a)).length;
  const overallPct = actions.length > 0 ? Math.round((completed / actions.length) * 100) : 0;

  return (
    <div className="space-y-3">
      {/* Summary strip */}
      <div className="flex flex-wrap items-center gap-4 rounded border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900/50">
        <div>
          <p className="text-[9px] font-semibold uppercase text-slate-400">Total</p>
          <p className="text-[14px] font-bold text-slate-800 dark:text-slate-100">{actions.length}</p>
        </div>
        <div>
          <p className="text-[9px] font-semibold uppercase text-slate-400">Concluídas</p>
          <p className="text-[14px] font-bold text-emerald-600">{completed}</p>
        </div>
        {overdue > 0 && (
          <div>
            <p className="text-[9px] font-semibold uppercase text-slate-400">Atrasadas</p>
            <p className="text-[14px] font-bold text-red-600">{overdue}</p>
          </div>
        )}
        <div className="ml-auto min-w-[120px]">
          <div className="flex items-center justify-between text-[10px]">
            <span className="text-slate-400">Execução</span>
            <span className="font-semibold text-slate-700 dark:text-slate-200">{overallPct}%</span>
          </div>
          <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
            <div className="h-full rounded-full bg-emerald-500" style={{ width: `${overallPct}%` }} />
          </div>
        </div>
      </div>

      {/* Action cards */}
      <div className="space-y-1.5">
        {visible.map(action => {
          const pct = actionCompletionPct(action);
          const overdueAction = isActionOverdue(action);
          const assignee = action.assigned_to ? (assigneeNames[action.assigned_to] ?? '—') : 'Não atribuído';

          return (
            <button
              key={action.id}
              type="button"
              onClick={() => onRowClick(action)}
              className={`group flex w-full items-center gap-3 rounded border px-3 py-2.5 text-left transition-colors ${
                overdueAction
                  ? 'border-red-200 bg-red-50/50 hover:bg-red-50 dark:border-red-900/50 dark:bg-red-950/20'
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800/60'
              }`}
            >
              <span className={`h-2 w-2 shrink-0 rounded-full ${STATUS_DOT[action.status] ?? 'bg-slate-400'}`} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[12px] font-semibold text-slate-800 dark:text-slate-100">{action.title}</p>
                <div className="mt-1 flex flex-wrap items-center gap-3 text-[10px] text-slate-500">
                  <span className="flex items-center gap-1"><User size={10} /> {assignee}</span>
                  {action.due_date && (
                    <span className={`flex items-center gap-1 ${overdueAction ? 'font-semibold text-red-600' : ''}`}>
                      <Calendar size={10} />
                      {new Date(action.due_date).toLocaleDateString('pt-BR')}
                      {overdueAction && <AlertCircle size={10} />}
                    </span>
                  )}
                  <span>{ACTION_STATUS_LABELS[action.status]}</span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <div className="hidden sm:block">
                  <div className="h-1 w-16 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                    <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="mt-0.5 text-right text-[9px] text-slate-400">{pct}%</p>
                </div>
                <ChevronRight size={14} className="text-slate-300 group-hover:text-slate-500" />
              </div>
            </button>
          );
        })}
      </div>

      {compact && actions.length > 5 && (
        <p className="text-center text-[10px] text-slate-400">+ {actions.length - 5} ações adicionais</p>
      )}

      {readOnly ? null : (
        <button
          type="button"
          onClick={onCreateAction}
          className="flex items-center gap-1 rounded border border-dashed border-slate-300 px-3 py-1.5 text-[11px] font-medium text-slate-600 hover:border-blue-300 hover:text-blue-600 dark:border-slate-600 dark:text-slate-400"
        >
          <Plus size={12} /> Nova ação corretiva
        </button>
      )}
    </div>
  );
};

export default ProblemActionsBlock;
